/**
 * Custom hook for managing URL query parameters with automatic URL synchronization
 *
 * @param key - The query parameter key to manage
 * @param defaultValue - The default value to use when the parameter is not in the URL and hasn't been changed
 * @returns [value, updateValue]
 * - value: Current value of the query parameter (defaultValue if not changed)
 * - updateValue: Function to update the query parameter and URL
 *
 * Features:
 * - Syncs state with URL query parameters
 * - Updates browser history without page reload
 * - Handles SSR safely
 * - Uses defaultValue until parameter is explicitly changed
 * - Detects URL changes from:
 *   - History API (replaceState)
 *
 * @example
 * ```tsx
 * const [nbEmployees, setNbEmployees] = useQueryParam('nbEmployee', '5');
 *
 * // Reading the value
 * console.log(nbEmployees); // "5" (default) or value from URL
 *
 * // Updating the value (also updates URL)
 * setNbEmployees('10'); // URL becomes ?nbEmployee=10
 * setNbEmployees(null); // Removes parameter from URL
 * ```
 */
import { useCallback, useState, useEffect } from 'react';
import { getQueryParams } from './getQueryParams';
import { createHistoryObserver } from './historyObserver';
import { updateUrlParams } from './updateUrlParams';

export type QueryParamValue = string | null;
export type SetQueryParamValue = (newValue: QueryParamValue) => void;
export type QueryParams = Record<string, QueryParamValue>;
export type SetQueryParams = (newParams: Partial<QueryParams>) => void;

// Shared logic for URL parameter management
const useUrlParamSync = (updateFromUrl: () => void) => {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Observer replaceState and pushState
    const cleanup = createHistoryObserver();

    // Update the state when the URL changes
    window.addEventListener('paramChange', updateFromUrl);

    // Add listener for popstate to handle back/forward navigation
    window.addEventListener('popstate', updateFromUrl);

    return () => {
      cleanup();
      window.removeEventListener('paramChange', updateFromUrl);
      window.removeEventListener('popstate', updateFromUrl);
    };
  }, [updateFromUrl]);
};

// Helper to update URL and trigger state update
const updateUrl = (params: Record<string, string>) => {
  if (typeof window === 'undefined') return;

  // Build new URL with updated parameters
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      searchParams.set(key, value);
    }
  });

  const newUrl =
    window.location.pathname +
    (searchParams.toString() ? `?${searchParams.toString()}` : '') +
    window.location.hash;

  // Update URL without reloading the page
  window.history.replaceState({}, '', newUrl);

  // Trigger update to sync state with URL
  window.dispatchEvent(new Event('paramChange'));
};

/**
 * Hook to manage a single URL query parameter
 */
export const useQueryParam = (
  key: string,
  defaultValue: QueryParamValue,
): [QueryParamValue, SetQueryParamValue] => {
  const [valueIsChanged, setValueIsChanged] = useState(false);
  const [value, setValue] = useState<QueryParamValue>(() => {
    return getQueryParams()[key] || null;
  });

  // Function to update the value from the URL
  const updateFromUrl = useCallback(() => {
    setValue(getQueryParams()[key] || null);
  }, [key]);

  // Set up URL sync
  useUrlParamSync(updateFromUrl);

  // Function to update the URL
  const updateValue = useCallback(
    (newValue: QueryParamValue) => {
      if (typeof window === 'undefined') return;
      setValueIsChanged(true);
      updateUrlParams(key, newValue);
      // Trigger update to sync state with URL
      window.dispatchEvent(new Event('paramChange'));
    },
    [key],
  );

  return [valueIsChanged || value ? value : defaultValue, updateValue];
};

/**
 * Hook to manage multiple URL query parameters at once
 * 
 * @returns [values, updateValues]
 * - values: Object with current values of all query parameters
 * - updateValues: Function to update multiple query parameters at once
 * 
 * @example
 * ```tsx
 * const [params, setParams] = useQueryParams();
 * 
 * // Reading values
 * console.log(params.nbEmployee); // Value from URL or null
 * 
 * // Updating values (also updates URL)
 * setParams({ nbEmployee: '10', country: 'US' }); // URL becomes ?nbEmployee=10&country=US
 * setParams({ nbEmployee: null }); // Removes nbEmployee parameter from URL
 * ```
 */
export const useQueryParams = (): [QueryParams, SetQueryParams] => {
  const [values, setValues] = useState<QueryParams>(() => {
    return getQueryParams();
  });

  // Function to update values from the URL
  const updateFromUrl = useCallback(() => {
    setValues(getQueryParams());
  }, []);

  // Set up URL sync
  useUrlParamSync(updateFromUrl);

  // Function to update multiple URL parameters at once
  const updateValues = useCallback(
    (newParams: Partial<QueryParams>) => {
      if (typeof window === 'undefined') return;

      // Get current params
      const currentParams = getQueryParams();

      // Update each parameter in the URL
      Object.entries(newParams).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          // Remove parameter if value is null or undefined
          delete currentParams[key];
        } else {
          // Update parameter value
          currentParams[key] = value;
        }
      });

      // Update URL with new parameters
      updateUrl(currentParams);
    },
    []
  );

  return [values, updateValues];
};
